import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import MUIDataTable from "mui-datatables";
import LoadingOverlay from "react-loading-overlay-ts";

// MUI Imports
import {
  Box,
  ThemeProvider,
  Grid,
  Card,
  Typography,
  CardContent,
  Paper,
  Container,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArchiveIcon from "@mui/icons-material/Archive";

// Project Imports
import { PrintButtonIcon } from "../../../../Components/Icons/DataTableIcons";
import ConfirmDialog from "../../../../Components/Alert/ConfirmDialog";
import { colors } from "../../../../Styles/GlobalStyles/Variables";
import { fShortenNumber } from "../../../../Utils/formatNumber";
import Iconify from "../../../../Components/Dashboard/Iconify";
import Page from "../../../../Components/Dashboard/Page";
import {
  ActivateIcon,
  DeactivateIcon,
  EditIcon,
  MuiDataTableTheme,
  ViewIcon,
} from "../../../../Components/MuiDataTableTheme/MuiDataTableTheme";
import { components } from "../../../../Components/Icons/DataTableIcons";
import { ElevatedGreenButton } from "../../../../Components/Button/CustomButton";
import { poppinsCustomTheme } from "../../../../Styles/GlobalStyles/PoppinsTheme";
import StatusGenerator from "../../../../Components/StatusGenerator/StatusGenerator";
import GeneralDialog from "../../../../Components/Partners/GeneralDialog";
import PrintLayout from "../../../../Components/Partners/PrintLayout";
import Searchbox from "../../../../Components/Dashboard/Searchbox";
import FilterButton from "../../../../Components/Button/FilterButton";

import {
  fetchAllPartners,
  makeRequestPartners,
  fetchPartnerDashboardDetails,
  activatePartner,
  deactivatePartner,
  restorePartnersInitial,
  exportPartner,
  fetchStates,
  fetchPartnerById,
  fetchDialogData,
  printPartners,
  searchTable,
} from "../../../../Actions";

const RootStyle = styled(Card)(({ theme, bgcolor = "#FFE2E5" }) => ({
  boxShadow: "none",
  textAlign: "center",
  padding: theme.spacing(3, 0),
  color: theme.palette.primary.darker,
  backgroundColor: bgcolor,
  borderRadius: "16px",
}));

const IconWrapperStyle = styled("div")(({ theme, bgcolor = "#FA5A7D" }) => ({
  margin: "auto",
  display: "flex",
  borderRadius: "50%",
  alignItems: "center",
  width: theme.spacing(5),
  height: theme.spacing(5),
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  color: "#fff",
  backgroundColor: bgcolor,
}));

const CardTotalPartners = ({
  value,
  title,
  bgcolor,
  iconColor,
  theIcon,
  id,
}) => {
  return (
    <RootStyle bgcolor={bgcolor} id={id}>
      <IconWrapperStyle bgcolor={iconColor}>
        <Iconify icon={theIcon} width={22} height={22} />
      </IconWrapperStyle>
      <Typography variant="h5" fontWeight={700} fontSize={"25px"}>
        {fShortenNumber(value)}
      </Typography>
      <Typography variant="subtitle2" sx={{ opacity: 0.72, fontSize: "12px" }}>
        {title}
      </Typography>
    </RootStyle>
  );
};

const ColumnTextStyle = {
  textTransform: "capitalize",
  textAlign: "left",
  color: "#212b36",
  fontSize: "0.875rem",
  fontWeight: 600,
};

const CustomToolbarSelect = () => {
  const handleClickArchive = () => {};
  return (
    <div style={{ marginRight: " 24px" }}>
      <Tooltip title={`Archive Partners`}>
        <IconButton onClick={handleClickArchive}>
          <ArchiveIcon />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const PartnerManagement = () => {
  const figureColumnStyle = {
    paddingRight: "70px",
    fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
  };

  const dispatch = useDispatch();
  const partnerState = (state) => state.partnersReducer;
  const listOfCities = (state) => state.statesReducer;

  const { statesfetched } = useSelector(listOfCities);
  const {
    allPartners,
    partnersDashboardDetails,
    tablePaginationData,
    fetchingDataGeneral,
    makingRequest,
    fetchingPartnerDialogData,
    allPartnersDataForPrinting,
  } = useSelector(partnerState);

  const [applySearch, setApplySearch] = useState(false);
  const [firePrint, setFirePrint] = useState(false);
  const [filters, setFilters] = useState("");
  const [filterOptions, setFilterOptions] = useState("[]");
  const [allStatesNG, setAllStatesNG] = useState([]);
  const [showTableLoader, setShowTableLoader] = useState(false);
  const [partnerDialogIsOpen, setPartnerDialogIsOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState(""); // Create || Edit || View
  const [partnersTableData, setPartnersTableData] = useState([]);
  const [printPartnersData, setPrintPartnersData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [tablePageData, setTablePageData] = useState({
    totalPages: 0,
    pageSize: 10,
    totalCount: 0,
    pageNumber: 1,
    nextPage: 0,
    previousPge: 0,
    firstPage: 0,
    lastPage: 2,
    search: "",

  });

  const [partnerData, setPartnerData] = useState({
    // dialog data used for API purposes
    name: "",
    stateId: null,
  });

  const [partnerCityData, setPartnerCityData] = useState({
    label: "",
    value: null,
  });

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    header: "",
    backgroundColor: "",
    borderSideColor: "",
    color: "",
    subColor: "",
  });

  const [dashboardData, setDashboardData] = useState({
    totalPartners: 0,
    totalActivePartners: 0,
    totalInactivePartners: 0,
  });

  //dialog form validation
  const [dialogFormErrors, setDialogFormErrors] = useState({
    name: false,
    state: false,
    dontFire: true,
  });

  const fireFetchAllPartnersAPI = () => {
    dispatch(
      fetchAllPartners({
        PageNumber: tablePageData.pageNumber,
        PageSize: tablePageData.pageSize,
        Search: searchText,
        Filters: filters
      })
    );
    dispatch(fetchPartnerDashboardDetails());
  };

  const activatePartnerHandler = (id) => {
    dispatch(activatePartner(id));
    dispatch(makeRequestPartners());
  };

  const deactivatePartnerHandler = (id) => {
    dispatch(deactivatePartner(id));
    dispatch(makeRequestPartners());
  };

  const closePartnersGeneralDialog = () => {
    setPartnerDialogIsOpen(false);
    setPartnerData({
      name: "",
      stateId: null,
    });
    setPartnerCityData({
      label: "",
      value: null,
    });
    setDialogFormErrors({
      ...dialogFormErrors,
      name: false,
      state: false,
      dontFire: true,
    });
  };

  const openAcquisitionDialog = (mode, partnerId = 0) => {
    setPartnerDialogIsOpen(true);
    setDialogMode(mode);
    // setEditPartnerId(partnerId);

    if (statesfetched.length < 1) {
      dispatch(fetchStates());
    }

    if (mode !== "Create") {
      dispatch(fetchPartnerById(partnerId));
      dispatch(fetchDialogData());
    }
  };

  const handlePrint = () => {
    dispatch(printPartners());
    setFirePrint(true);
  };

  const handleSearchChange = (event) => {
    const newSearchText = event.target.value;
    setSearchText(newSearchText);
    setApplySearch(true);
  };

  const handleFilterClick = (event) => {
    setFilters(event);
    setFilterOptions(event);

    dispatch(
      fetchAllPartners({
        PageNumber: tablePageData.pageNumber,
        PageSize: tablePageData.pageSize,
        Search: searchText,
        Filters: event
      })
    );
    // dispatch(makeRequestPartners())
  };
  
  const handleReset = () => {
    dispatch(
      fetchAllPartners({
        PageNumber: tablePageData.pageNumber,
        PageSize: tablePageData.pageSize,
        Search: searchText,
        Filters: {}
      })
    );
    
    // dispatch(makeRequestPartners())
    setFilterOptions("[]");
    setFilters("[]");
  };

  const cards = [
    {
      title: "Total Partners",
      value: dashboardData.totalPartners,
      icon: "null",
      bgcolor: "#C8E7FD",
      iconColor: "#0095FF",
      theIcon: "fluent:people-team-16-filled",
      id: "total-partners",
    },
    {
      title: "Active Partners",
      value: dashboardData.totalActivePartners,
      icon: "null",
      bgcolor: "#CBE1D7",
      iconColor: "#016C34",
      theIcon: "fluent:people-checkmark-16-filled",
      id: "active-partners",
    },
    {
      title: "Inactive Partners",
      value: dashboardData.totalInactivePartners,
      icon: "null",
      bgcolor: "#FFE2E5",
      iconColor: "#DC2626",
      theIcon: "fluent:people-team-delete-32-filled",
      id: "inactive-partners",
    },
  ];

  const columns = [
    {
      name: "partners",
      label: "Partner Name",
      options: {
        setCellProps: () => ({
          style: { paddingLeft: "24px" },
        }),
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography
              style={{ paddingLeft: "24px" }}
              sx={ColumnTextStyle}
              key={index}
            >
              {column.label}
            </Typography>
          );
        },
      },
    },
    {
      name: "email",
      label: "Email Address",
      options: {
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
      },
    },
    {
      name: "totalLoanGranted",
      label: "Loans Granted",
      options: {
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value, tableMeta) => {
          return (
            <Typography align="right" sx={figureColumnStyle}>
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "totalLoanBeneficiaries",
      label: "Loan Beneficiaries",
      options: {
        filter: true,
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value, tableMeta) => {
          return(
            <Typography align="right" sx={figureColumnStyle} >
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        sort: false,
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value, tableMeta) => {
          switch (value) {
            case 1:
              return (
                <StatusGenerator statusText="Active" variant="greenFilled" />
              );
            case 0:
              return (
                <StatusGenerator statusText="Inactive" variant="redFilled" />
              );
            default:
              return "no status";
          }
        },
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        sort: false,
        filterType: false,
        filter: false,
        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              {column.label}
            </Typography>
          );
        },
        customBodyRender: (value, tableMeta) => {
          const centreName = tableMeta.rowData[0];

          let [centreIsActive, partnerId] = value.split(" ");

          centreIsActive = Number.parseInt(centreIsActive);
          partnerId = Number.parseInt(partnerId);
          return (
            <>
              <Tooltip title={"View Partner"}>
                <IconButton
                  size="small"
                  onClick={() => {
                    openAcquisitionDialog("View", partnerId);
                  }}
                >
                  <ViewIcon icon="material-symbols:visibility-outline" />
                </IconButton>
              </Tooltip>
              <Tooltip title={"Edit Partner"}>
                <IconButton
                  size="small"
                  onClick={() => openAcquisitionDialog("Edit", partnerId)}
                >
                  <EditIcon icon="icon-park-outline:edit" />
                </IconButton>
              </Tooltip>
              {centreIsActive === 0 ? (
                <Tooltip title={"Activate Partner"}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setConfirmDialog({
                        isOpen: true,
                        header: `Activate Partner: ${centreName}`,
                        title: `Are you sure you want to activate ${centreName}?`,
                        backgroundColor: colors.successDialogBg,
                        borderSideColor: colors.darkSuccessDialog,
                        color: colors.primaryGreen,
                        subColor: colors.successDialogSubTitle,
                        onConfirm: () => {
                          activatePartnerHandler(partnerId);
                        },
                      })
                    }
                  >
                    <ActivateIcon icon="fluent-mdl2:activate-orders" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={"Deactivate Partner"}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setConfirmDialog({
                        isOpen: true,
                        header: `Deactivate Partner: ${centreName} `,
                        title: `Are you sure you want to deactivate ${centreName}?`,
                        backgroundColor: colors.errorDialogBg,
                        borderSideColor: colors.darkerErrorDialog,
                        color: colors.darkerErrorDialog,
                        subColor: colors.darkerErrorDialog,
                        onConfirm: () => {
                          deactivatePartnerHandler(partnerId);
                        },
                      })
                    }
                  >
                    <DeactivateIcon icon="fluent-mdl2:deactivate-orders" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        },
      },
    },
  ];

  const filterOption = [
    // {
    //   type: "zoneAutocomplete",
    //   name: "zones",
    //   group: "Select Zones",
    //   options: [
      
    //   ],
    // },
    {
      type: "autocomplete",
      name: "states",
      group: "Select States",
      options: [],
    },
    {
      type: "radio",
      name: "status",
      group: "Status",
      options: [
        {
          label: "All",
          value: "",
        },
        {
          label: "Active",
          value: true,
        },
        {
          label: "Inactive",
          value: false,
        },
      ],
    },
  ];

  const options = {
    filter: false,
    count: tablePaginationData.totalCount,
    expandableRows: false,
    expandableRowsHeader: false,
    expandableRowsOnClick: false,
    disableRipple: true,
    elevation: 0,
    filterType: "dropdown",
    fixedHeader: true,
    jumpToPage: true,
    page: tablePageData.pageNumber - 1,
    pagination: true,
    print: false,

    responsive: "standard",
    rowsPerPage: 10,
    rowsPerPageOptions: [10],
    search: false,
    selectableRows: "none",
    serverSide: true,
    tableBodyHeight: "600px",
    tableBodyMaxHeight: "1000px",
    onDownload: () => {
      dispatch(exportPartner());
      return false;
    },
    customToolbar: () => {
      return (
        <>
          <Tooltip title="Print All Partners">
            <IconButton
              onClick={() => {
                handlePrint();
              }}
            >
              <PrintButtonIcon />
            </IconButton>
          </Tooltip>
          <FilterButton
            options={filterOption}
            filters={filters}
            onClick={handleFilterClick}
            reset={handleReset}
          />
          <ElevatedGreenButton
            startIcon={<AddOutlinedIcon />}
            onClick={() => openAcquisitionDialog("Create")}
          >
            Add New Partners
          </ElevatedGreenButton>
        </>
      );
    },
    customToolbarSelect: () => <CustomToolbarSelect />,

    onChangePage: (currentPage) => {
      document
        .getElementById("total-partners")
        .scrollIntoView({ behavior: "smooth" });

      setTablePageData({
        ...tablePageData,
        pageNumber: currentPage + 1,
      });

      dispatch(
        fetchAllPartners({
          PageNumber: currentPage + 1,
          PageSize: tablePageData.pageSize,
          Search: "",
        })
      );

      setShowTableLoader(true);
      // dispatch(makeRequestPartners())
    },

    textLabels: {
      body: {
        noMatch: "Sorry, no matching records found",
        toolTip: "Sort",
        columnHeaderTooltip: (column) => `Sort for ${column.label}`,
      },
      pagination: {
        next: "Next Page",
        previous: "Previous Page",
        rowsPerPage: "Rows per page:",
        displayRows: "of",
      },
      toolbar: {
        search: "Search",
        downloadCsv: "Download sheet",
        print: "Print page",
        viewColumns: "View columns",
        filterTable: "Filter table",
      },
      filter: {
        all: "All",
        title: "FILTERS",
        reset: "RESET",
      },
      viewColumns: {
        title: "Show Columns",
        titleAria: "Show/Hide Table Columns",
      },
      selectedRows: {
        text: "row(s) selected",
        delete: "Delete",
        deleteAria: "Delete Selected Rows",
      },
    },
  };

  useEffect(() => {
    if (Array.isArray(allPartners)) {
      let tempTableData = [];

      setShowTableLoader(false);

      allPartners.map((centre) => {
        // 1. populate table data
        let centreData = [
          centre.name,
          centre.email,
          centre.totalLoanGranted,
          centre.totalLoanBeneficiaries,
          // centre.dateCreated,
          centre.isActive === true ? 1 : 0,
          `${centre.isActive === true ? 1 : 0} ${centre.id}`,
        ];
        tempTableData.push(centreData);

        return null;
      });

      setPartnersTableData(tempTableData);

      // set pagination data for datatable
      if (Object.keys(tablePaginationData).length > 0) {
        setTablePageData(tablePaginationData);
      }

      // update dashboard values with real data
      if (Object.keys(partnersDashboardDetails).length > 0) {
        setDashboardData(partnersDashboardDetails);
      }
    }

    return () => {
      dispatch(restorePartnersInitial());
    };
  }, [allPartners, tablePaginationData, partnersDashboardDetails, dispatch]);

  useEffect(() => {
    if (makingRequest === false) {
      fireFetchAllPartnersAPI();
      setConfirmDialog({
        ...confirmDialog,
        isOpen: false,
      });
      setPartnerDialogIsOpen(false);
    }
  }, [makingRequest]);

  useEffect(() => {
    if (statesfetched.length > 0) {
      let statesModel = [];

      statesfetched.map((state) => {
        statesModel.push({
          label: state.stateName,
          value: state.stateID,
        });
      });

      statesModel.push({
        label: "",
        value: null,
      });

      setAllStatesNG(statesModel);
    }
  }, [statesfetched]);

  useEffect(() => {
    if (Object.keys(allPartnersDataForPrinting).length > 0) {
      setPrintPartnersData(allPartnersDataForPrinting);
    }
  }, [allPartnersDataForPrinting]);

  useEffect(() => {
    if (printPartnersData.length > 0 && firePrint === true) {
      var iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.srcdoc = document.getElementById("print-template").innerHTML;
      document.body.appendChild(iframe);
      iframe.contentWindow.print();
    }

    return () => {
      setFirePrint(false);
    };
  }, [printPartnersData]);

  useEffect(() => {
    if (applySearch === true) {
      dispatch(searchTable());
      fireFetchAllPartnersAPI();
    }
  }, [searchText]);


  return (
    <ThemeProvider theme={poppinsCustomTheme}>
      <Page title="SAED | Partners">
        <Page>
          <Container maxWidth="xl">
            <Box sx={{ pb: 2 }}>
              <Typography
                variant="h5"
                style={{ fontWeight: 600, color: "#212B36" }}
              >
                Partners Management
              </Typography>
            </Box>
            <Box sx={{ pb: 2 }}>
              <Grid container spacing={3}>
                {cards.map((card) => {
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md
                      key={`${card.title}-${card.value}`}
                    >
                      <CardTotalPartners
                        value={card.value}
                        title={card.title}
                        bgcolor={card.bgcolor}
                        iconColor={card.iconColor}
                        theIcon={card.theIcon}
                        id={card.id}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box>
                  <Paper
                    sx={{
                      boxShadow: "0 1px 2px 0 rgb(145 158 171 / 24%)",
                      borderRadius: "20px",
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{ padding: "0px !important", borderRadius: "20px" }}
                    >
                      <CardContent sx={{ padding: "0px !important" }}>
                        <ThemeProvider theme={MuiDataTableTheme}>
                          <LoadingOverlay
                            active={
                              fetchingDataGeneral ||
                              makingRequest ||
                              showTableLoader
                            }
                            styles={{
                              overlay: (base) => ({
                                ...base,
                                background: "rgba(0, 0, 0, 0.15)",
                              }),
                            }}
                            spinner
                            text="Fetching Data for You..."
                          >
                            <div id="partners-datatable">
                              <MUIDataTable
                                title={
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={10}
                                    justifyContent="space-between"
                                  >
                                    <Typography
                                      sx={{ color: "#05004E", fontWeight: 600 }}
                                    >
                                      Partners
                                    </Typography>
                                    <Searchbox
                                      value={searchText}
                                      onChange={handleSearchChange}
                                    />
                                  </Box>
                                }
                                columns={columns}
                                options={options}
                                components={components}
                                style={{ transition: "0.7s" }}
                                data={partnersTableData}
                              />
                            </div>
                          </LoadingOverlay>
                        </ThemeProvider>
                      </CardContent>
                    </Card>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Container>
          <GeneralDialog
            isOpen={partnerDialogIsOpen}
            isFetchingDialogData={fetchingPartnerDialogData}
            onCloseHandler={closePartnersGeneralDialog}
            stateHolder={partnerCityData}
            stateSetter={(partnerCity) => { 
              setPartnerCityData(partnerCity);
              setPartnerData({ ...partnerData, stateId: partnerCity?.value });
            }}
            states={allStatesNG}
            mode={dialogMode}
            modeSetter={setDialogMode}
            fireFetchAllPartnersAPI={fireFetchAllPartnersAPI}
          />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <div id="print-template" style={{ display: "none" }}>
            <PrintLayout data={printPartnersData} />
          </div>
        </Page>
      </Page>
    </ThemeProvider>
  );
};

export default PartnerManagement;
